import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.div`
  background: #F5EDE3;
  border-bottom: 1px solid #E8D5B5;
`;

const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 1rem;
`;

const TableHeaderCell = styled.div`
  color: #4A3C31;
  font-weight: 600;
  cursor: ${props => props.$sortable ? 'pointer' : 'default'};
  
  &:hover {
    color: ${props => props.$sortable ? '#2C1810' : '#4A3C31'};
  }
`;

const TableBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 1rem;
  border-bottom: 1px solid #E8D5B5;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background: #E3F2FD;
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.25);
    transform: translateY(-2px);
    border-left: 4px solid #2196F3;
  }
  
  background: ${props => props.$selected ? '#E3F2FD' : 'white'};
  font-weight: ${props => props.$selected ? '700' : 'normal'};
  border-left: ${props => props.$selected ? '4px solid #2196F3' : '4px solid transparent'};
`;

const TableCell = styled.div`
  color: #2C1810;
`;

const SortIcon = styled.span`
  margin-left: 0.5rem;
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-${props => props.$direction === 'asc' ? 'bottom' : 'top'}: 4px solid #4A3C31;
  opacity: ${props => props.$active ? '1' : '0.3'};
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-top: 1px solid #E8D5B5;
`;

const PageButton = styled.button`
  background: none;
  border: none;
  color: #4A3C31;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  opacity: ${props => props.disabled ? '0.5' : '1'};
  
  &:hover {
    color: #2C1810;
  }
  
  &:disabled {
    cursor: default;
  }
`;

const PageNumber = styled.button`
  background: ${props => props.$active ? '#A68B5B' : 'none'};
  color: ${props => props.$active ? 'white' : '#4A3C31'};
  border: 1px solid #A68B5B;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  min-width: 2rem;
  text-align: center;
  
  &:hover {
    background: ${props => props.$active ? '#A68B5B' : '#F5EDE3'};
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const TableLayout = ({ 
  columns, 
  data, 
  loading = false, 
  onRowClick, 
  selectedRow,
  onSort,
  sortField, 
  sortDirection,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  pageSize = 10
}) => {
  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PageNumber
          key={i}
          $active={i === currentPage}
          onClick={() => onPageChange(i)}
        >
          {i}
        </PageNumber>
      );
    }

    return pages;
  };

  return (
    <TableContainer>
      <TableWrapper>
        <TableHeader>
          <TableHeaderRow>
            {columns.map(column => (
              <TableHeaderCell
                key={column.field}
                $sortable={column.sortable}
                onClick={() => column.sortable && onSort?.(column.field)}
              >
                {column.label}
                {column.sortable && (
                  <SortIcon
                    $direction={sortDirection}
                    $active={sortField === column.field}
                  />
                )}
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {data.map((row, index) => (
            <TableRow 
              key={index} 
              onClick={() => onRowClick?.(row)}
              $selected={selectedRow === row.order_number}
            >
              {columns.map((col, colIndex) => (
                <TableCell key={colIndex}>
                  {col.render ? col.render(row) : row[col.field]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <PaginationContainer>
          <PageButton
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </PageButton>
          <PageNumbers>
            {renderPageNumbers()}
          </PageNumbers>
          <PageButton
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </PageButton>
        </PaginationContainer>
      </TableWrapper>
    </TableContainer>
  );
};

export default TableLayout;
